import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { NavHamburger } from '@/components/Layouts';
import styles from './index.module.less';
import { AppSettings } from '@/shared/app-common';
import { useRouter } from 'next/router';
import { useUserProvider } from '@/context/UserProvider';
import { UpOutlined, RightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import type {
  MarketplaceNavItem,
  NavMenuItem,
  NavMenuGroup,
  NavMenuTab,
  NavMenuSection,
  NavIconType,
} from '@/components/Layouts/NavItem';
import SvgIcon from '@/components/Layouts/HeaderDropdown/SvgIcon';

// 使用 dynamic 导入并禁用 SSR，避免水合错误
const NavHamburgerDash = dynamic(() => import('@/components/Layouts/NavHamburgerDash'), {
  ssr: false,
});

const MobileHeader = ({ navItems }: { navItems: MarketplaceNavItem[] }) => {
  const [expandedMenu, setExpandedMenu] = useState<string>(''); // 展开的一级菜单
  const [activeTab, setActiveTab] = useState<string>(''); // tabs 布局中的活跃 tab
  const [initialized, setInitialized] = useState<boolean>(false); // 是否已初始化
  const router = useRouter();
  const { isLogin, userInfo, onSignOut, onOpenLoginModal } = useUserProvider();
  const { t } = useTranslation();

  const computedPathName = (pathname: string) => {
    if (pathname === '/') {
      return true;
    }
    let flag = false;
    const routerArr = ['/dmca-protection-badge', '/compare', '/content-monitoring', '/dmca-takedown', '/creators'];
    for (let i = 0; i < routerArr.length; i++) {
      if (pathname.indexOf(routerArr[i]) > -1) {
        flag = true;
        break;
      }
    }
    return flag;
  };

  // 根据当前路径找到匹配的菜单项
  const findActiveMenuByPath = useCallback(
    (pathname: string): { menuText: string; tabKey: string } | null => {
      for (const navItem of navItems) {
        if (!navItem.dropdownConfig) continue;

        const { layout, groups, tabs } = navItem.dropdownConfig;

        // 检查 columns/simple 布局的 groups
        if ((layout === 'columns' || layout === 'simple') && groups) {
          for (const group of groups) {
            if (group.items.some((item) => item.path && pathname.includes(item.path))) {
              return { menuText: navItem.text, tabKey: '' };
            }
          }
        }

        // 检查 tabs 布局
        if (layout === 'tabs' && tabs) {
          for (const tab of tabs) {
            for (const section of tab.sections) {
              // 检查 section 路径
              if (section.path && pathname.includes(section.path)) {
                return { menuText: navItem.text, tabKey: tab.key };
              }
              // 检查 section 内的 items
              if (section.items.some((item) => item.path && pathname.includes(item.path))) {
                return { menuText: navItem.text, tabKey: tab.key };
              }
            }
          }
        }
      }
      return null;
    },
    [navItems],
  );

  // 初始化时根据当前路径设置展开状态
  useEffect(() => {
    if (initialized || !navItems.length) return;

    const result = findActiveMenuByPath(router.pathname);
    if (result) {
      setExpandedMenu(result.menuText);
      setActiveTab(result.tabKey);
    }
    setInitialized(true);
  }, [router.pathname, navItems, initialized, findActiveMenuByPath]);

  // 渲染图标
  const renderIcon = (icon: NavIconType | undefined, className?: string, size: number = 16) => {
    if (!icon) return null;
    return <SvgIcon src={icon} className={className} size={size} />;
  };

  // 处理菜单项点击
  const handleItemClick = (item: NavMenuItem) => {
    if (!item.path) return;
    if (item.isExternal) {
      window.open(item.path, '_blank');
    } else {
      router.push(item.path);
    }
  };

  // 处理 section 标题点击
  const handleSectionTitleClick = (e: React.MouseEvent, section: NavMenuSection) => {
    if (section.path) {
      // 有链接时跳转，让事件冒泡关闭侧边栏
      router.push(section.path);
    } else {
      // 没有链接时阻止冒泡
      e.stopPropagation();
    }
  };

  // 检测菜单项是否为当前活跃项
  const isMenuItemActive = (item: NavMenuItem): boolean => {
    if (!item.path) return false;
    return router.pathname.includes(item.path) || router.asPath.includes(item.path);
  };

  // 渲染单个菜单项（带图标）
  const renderMenuItem = (item: NavMenuItem, index: number) => {
    const isActive = isMenuItemActive(item);
    return (
      <div
        key={index}
        className={classNames(styles.menuItem, { [styles.menuItemActive]: isActive })}
        onClick={(e) => {
          // 没有链接时阻止冒泡，避免关闭侧边栏
          if (!item.path) {
            e.stopPropagation();
            return;
          }
          // 有链接时正常跳转，让事件冒泡关闭侧边栏
          handleItemClick(item);
        }}
      >
        {renderIcon(item.icon, styles.menuItemIcon)}
        <span className={styles.menuItemText}>{item.text}</span>
      </div>
    );
  };

  // 渲染 columns 布局（Solutions）
  const renderColumnsLayout = (groups: NavMenuGroup[]) => {
    return (
      <div className={styles.columnsLayout}>
        {groups.map((group, groupIndex) => (
          <div key={groupIndex} className={styles.menuGroup}>
            {group.title && (
              <div className={styles.groupTitle} onClick={(e) => e.stopPropagation()}>
                {group.title}
              </div>
            )}
            <div className={styles.groupItems}>
              {group.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 渲染 simple 布局（Resources/Contact）
  // 分组标题与 columns 布局同样处理：Resources 下拉分了 Resources / Cooperation 两组，
  // 早前 simple 布局的分组 title 都是空串，故这里一直没渲染
  const renderSimpleLayout = (groups: NavMenuGroup[]) => {
    return (
      <div className={styles.simpleLayout}>
        {groups.map((group, groupIndex) => (
          <div key={groupIndex} className={styles.simpleGroup}>
            {group.title && (
              <div className={styles.groupTitle} onClick={(e) => e.stopPropagation()}>
                {group.title}
              </div>
            )}
            {group.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
          </div>
        ))}
      </div>
    );
  };

  // 渲染 tabs 布局（Tools）- 手风琴展开效果
  const renderTabsLayout = (tabs: NavMenuTab[]) => {
    return (
      <div className={styles.tabsLayout}>
        {tabs.map((tab) => (
          <div key={tab.key} className={styles.tabAccordion}>
            {/* Tab 头部 */}
            <div
              className={classNames(styles.tabItem, {
                [styles.tabItemActive]: activeTab === tab.key,
              })}
              onClick={(e) => {
                e.stopPropagation();
                // 点击切换展开/收起
                setActiveTab(activeTab === tab.key ? '' : tab.key);
              }}
            >
              <div className={styles.tabItemLeft}>
                {renderIcon(tab.icon, styles.tabIcon, 20)}
                <span className={styles.tabLabel}>{tab.label}</span>
              </div>
              <RightOutlined className={styles.tabArrow} />
            </div>

            {/* Tab 内容 - 只在当前 tab 展开时显示 */}
            {activeTab === tab.key && (
              <div className={styles.tabContent}>
                {tab.sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className={styles.tabSection}>
                    {section.title && (
                      <div
                        className={classNames(styles.sectionTitle, {
                          [styles.clickable]: !!section.path,
                        })}
                        onClick={(e) => handleSectionTitleClick(e, section)}
                      >
                        {section.title}
                        {section.path && <span className={styles.sectionArrow}> →</span>}
                      </div>
                    )}
                    <div className={styles.sectionItems}>
                      {section.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // 渲染下拉内容
  const renderDropdownContent = (item: MarketplaceNavItem) => {
    const config = item.dropdownConfig;
    if (!config) return null;

    switch (config.layout) {
      case 'columns':
        return renderColumnsLayout(config.groups || []);
      case 'simple':
        return renderSimpleLayout(config.groups || []);
      case 'tabs':
        return renderTabsLayout(config.tabs || []);
      default:
        return null;
    }
  };

  const getTitle = (): any => {
    const pathname = router.pathname;
    const arr = [
      {
        url: 'dashboard/privacy-protection/portrait',
        desc: t('Dashboard'),
      },
      {
        url: 'dashboard',
        desc: 'Overview',
      },
      {
        url: 'leaks-found',
        desc: 'Leaks Found',
      },
      {
        url: 'safe-links',
        desc: 'Safe Links',
      },
      {
        url: 'social-media',
        desc: 'Social Media',
      },
      {
        url: 'profile',
        desc: 'Setting',
      },
      {
        url: 'protected-Accounts',
        desc: t('Protect Content'),
      },
      {
        url: 'setting',
        desc: 'Setting',
      },
    ];
    const index = arr.findIndex((item) => pathname.includes(item.url));
    return index == -1 ? -1 : arr[index];
  };

  return (
    <div
      className={classNames(styles.mobileHeaderWarp, {
        [styles.whiteBackground]: computedPathName(router.pathname) || router.pathname.indexOf('report/') > -1,
      })}
    >
      {getTitle() == -1 ? (
        <div className={styles.navToolbar}>
          <div className={styles.navToolbarSection}>
            <NavHamburger>
              <div className={styles.navMenuList}>
                {navItems.map((item: MarketplaceNavItem, index: number) => (
                  <div key={index} className={styles.navItem}>
                    {/* 一级菜单项 */}
                    <div
                      className={styles.navItemHeader}
                      onClick={(e) => {
                        // 没有下拉菜单的直接跳转
                        if (!item.dropdownConfig) {
                          if (item.path) {
                            router.push(item.path);
                          }
                          return;
                        }
                        e.stopPropagation();
                        if (expandedMenu === item.text) {
                          setExpandedMenu('');
                          setActiveTab('');
                        } else {
                          setExpandedMenu(item.text);
                          setActiveTab(''); // 清空 activeTab，不默认展开任何子项
                        }
                      }}
                    >
                      <div
                        className={classNames(styles.navItemText, {
                          [styles.active]: expandedMenu === item.text,
                        })}
                      >
                        {item.text}
                      </div>
                      {item.dropdownConfig && (
                        <div
                          className={classNames(styles.navItemArrow, {
                            [styles.expanded]: expandedMenu === item.text,
                          })}
                        >
                          <UpOutlined />
                        </div>
                      )}
                    </div>

                    {/* 下拉内容 */}
                    {expandedMenu === item.text && item.dropdownConfig && (
                      <div className={styles.dropdownContent}>{renderDropdownContent(item)}</div>
                    )}
                  </div>
                ))}
                {isLogin ? (
                  <div
                    className={styles.signUp}
                    onClick={() => {
                      onSignOut();
                    }}
                  >
                    {t('Sign out')}
                  </div>
                ) : (
                  <div
                    className={styles.signUp}
                    onClick={() => {
                      if (!isLogin) {
                        onOpenLoginModal(true);
                      }
                    }}
                  >
                    {t('Sign up')}
                  </div>
                )}
              </div>
            </NavHamburger>
          </div>
          <div className={styles.navToolbarSection}>
            <Link href="/" className={styles.logo}>
              <Image
                src={AppSettings.namePath}
                alt={AppSettings.name}
                style={{ verticalAlign: 'middle' }}
                priority
                width={114}
                height={42}
              />
            </Link>
          </div>
          <div className={styles.navToolbarSection}>
            {isLogin ? (
              <div className={styles.avatar} onClick={() => router.push('/dashboard')}>
                {userInfo?.first_name?.charAt(0) || 'C'}
              </div>
            ) : (
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!isLogin) {
                    onOpenLoginModal(true);
                  }
                }}
              >
                {t('Login')}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.navToolbar}>
          <div className={styles.navToolbarSection}>
            <NavHamburgerDash>
              <></>
            </NavHamburgerDash>
          </div>
          <div className={styles.desc}>{getTitle().desc}</div>
          {/* <div className={styles.navToolbarSection}>
                <div
                  style={{
                    height: "2rem",
                    width: "2rem",
                    textAlign: "center",
                    lineHeight: "2rem",
                    backgroundColor: "var(--app-primary-color)",
                    borderRadius: "2rem",
                  }}
                >
                  {userInfo?.first_name?.charAt(0) || "C"}
                </div>
              </div> */}
          <NavHamburger iNode={<></>}>
            <></>
          </NavHamburger>
        </div>
      )}
    </div>
  );
};
export default MobileHeader;
